import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { visitsApi, OwnershipFilterValue, VisitPriority } from '@/api/visits';

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  // Axios error with response
  const axiosError = error as { response?: { data?: { message?: string } } };
  if (axiosError.response?.data?.message) {
    return axiosError.response.data.message;
  }
  return 'Произошла ошибка';
}

type FilterValue = OwnershipFilterValue[] | 'all';

export function useWeeklyVisits(
  ownershipFilters: FilterValue = ['own'],
  weekCode?: string
) {
  return useQuery({
    queryKey: ['visits', 'weekly', weekCode, ownershipFilters],
    queryFn: () => visitsApi.getWeeklyVisits(weekCode, ownershipFilters),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
}

export function useMarkVisited() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ clientId, note, visitedAt }: { clientId: number; note?: string; visitedAt?: string }) =>
      visitsApi.markVisited(clientId, note, visitedAt),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['visits', 'weekly'] });
      queryClient.invalidateQueries({ queryKey: ['clientWeeklyVisits'] });
      queryClient.invalidateQueries({ queryKey: ['client-detail'] });
    },
    onError: (error) => {
      toast.error('Ошибка отметки визита', { description: getErrorMessage(error) });
    },
  });
}

export function useMarkSkipped() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ clientId, note }: { clientId: number; note?: string }) =>
      visitsApi.markSkipped(clientId, note),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['visits', 'weekly'] });
      queryClient.invalidateQueries({ queryKey: ['clientWeeklyVisits'] });
      queryClient.invalidateQueries({ queryKey: ['client-detail'] });
    },
    onError: (error) => {
      toast.error('Ошибка пропуска визита', { description: getErrorMessage(error) });
    },
  });
}

export function useUndoCheckIn() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (clientId: number) => visitsApi.undoCheckIn(clientId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['visits', 'weekly'] });
      queryClient.invalidateQueries({ queryKey: ['visits', 'active'] });
      queryClient.invalidateQueries({ queryKey: ['clientWeeklyVisits'] });
      queryClient.invalidateQueries({ queryKey: ['client-detail'] });
    },
    onError: (error) => {
      toast.error('Ошибка отмены check-in', { description: getErrorMessage(error) });
    },
  });
}

export function useCancelVisit() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (visitTargetId: number) => visitsApi.cancelVisit(visitTargetId),
    onMutate: async (visitTargetId) => {
      await queryClient.cancelQueries({ queryKey: ['visits', 'weekly'] });
      const previousData = queryClient.getQueriesData({ queryKey: ['visits', 'weekly'] });

      // Optimistically remove the visit (set to cancelled state)
      queryClient.setQueriesData({ queryKey: ['visits', 'weekly'] }, (old: unknown) => {
        if (!old || typeof old !== 'object') return old;
        const data = old as { visits?: Array<{ visitTargetId: number; visit?: object }> };
        if (!data.visits) return old;
        return {
          ...data,
          visits: data.visits.map((item) =>
            item.visitTargetId === visitTargetId
              ? { ...item, visit: undefined }
              : item
          ),
        };
      });

      return { previousData };
    },
    onError: (error, _variables, context) => {
      if (context?.previousData) {
        for (const [queryKey, data] of context.previousData) {
          queryClient.setQueryData(queryKey, data);
        }
      }
      toast.error('Ошибка отмены визита', { description: getErrorMessage(error) });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['visits', 'weekly'] });
      queryClient.invalidateQueries({ queryKey: ['visits', 'active'] });
      queryClient.invalidateQueries({ queryKey: ['clientWeeklyVisits'] });
      queryClient.invalidateQueries({ queryKey: ['client-detail'] });
    },
  });
}

export function usePlanToday() {
  const queryClient = useQueryClient();
  const todayStr = new Date().toISOString().split('T')[0];

  return useMutation({
    mutationFn: (visitTargetId: number) => visitsApi.planToday(visitTargetId),
    onMutate: async (visitTargetId) => {
      await queryClient.cancelQueries({ queryKey: ['visits', 'weekly'] });
      const previousData = queryClient.getQueriesData({ queryKey: ['visits', 'weekly'] });

      queryClient.setQueriesData({ queryKey: ['visits', 'weekly'] }, (old: unknown) => {
        if (!old || typeof old !== 'object') return old;
        const data = old as { visits?: Array<{ visitTargetId: number; deadlineStatus?: string | null; visit?: { plannedDate?: string | null; deadline?: string | null; status?: string } }> };
        if (!data.visits) return old;
        return {
          ...data,
          visits: data.visits.map((item) =>
            item.visitTargetId === visitTargetId
              ? {
                  ...item,
                  deadlineStatus: 'today' as const,
                  visit: {
                    ...item.visit,
                    plannedDate: null,
                    deadline: todayStr,
                    status: item.visit?.status || 'PENDING',
                  },
                }
              : item
          ),
        };
      });

      return { previousData };
    },
    onError: (error, _variables, context) => {
      if (context?.previousData) {
        for (const [queryKey, data] of context.previousData) {
          queryClient.setQueryData(queryKey, data);
        }
      }
      toast.error('Ошибка планирования', { description: getErrorMessage(error) });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['visits', 'weekly'] });
      queryClient.invalidateQueries({ queryKey: ['clientWeeklyVisits'] });
      queryClient.invalidateQueries({ queryKey: ['client-detail'] });
    },
  });
}

export function useScheduleVisit() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      visitTargetId,
      date,
      deadline,
      plannedTime,
    }: {
      visitTargetId: number;
      date: string;
      deadline?: string;
      plannedTime?: string;
    }) => visitsApi.scheduleVisit(visitTargetId, date, deadline, plannedTime),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['visits', 'weekly'] });
      queryClient.invalidateQueries({ queryKey: ['clientWeeklyVisits'] });
      queryClient.invalidateQueries({ queryKey: ['client-detail'] });
      queryClient.invalidateQueries({ queryKey: ['client-planned-visits'] });
      toast.success('Визит запланирован');
    },
    onError: (error) => {
      toast.error('Ошибка планирования', { description: getErrorMessage(error) });
    },
  });
}

// Запланированные визиты конкретного клиента (PENDING с plannedDate)
export function useClientPlannedVisits(clientId: number, enabled = true) {
  return useQuery({
    queryKey: ['client-planned-visits', clientId],
    queryFn: () => visitsApi.getClientPlannedVisits(clientId),
    enabled: enabled && !!clientId,
    staleTime: 30_000,
  });
}

export function useSetClientPreference() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      clientId,
      preference,
    }: {
      clientId: number;
      preference: {
        officeId?: string | null;
        preferredDayOfWeek?: number | null;
        preferredTime?: string | null;
        note?: string | null;
      };
    }) => visitsApi.setClientPreference(clientId, preference),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['visits', 'weekly'] });
      queryClient.invalidateQueries({ queryKey: ['client-detail'] });
      toast.success('Настройки сохранены');
    },
    onError: (error) => {
      toast.error('Ошибка сохранения', { description: getErrorMessage(error) });
    },
  });
}

export function useSetPriority() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      visitTargetId,
      priority,
    }: {
      visitTargetId: number;
      priority: VisitPriority;
    }) => visitsApi.setPriority(visitTargetId, priority),
    onMutate: async ({ visitTargetId, priority }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['visits', 'weekly'] });

      // Snapshot previous value
      const previousData = queryClient.getQueriesData({ queryKey: ['visits', 'weekly'] });

      // Optimistically update weekly visits
      queryClient.setQueriesData({ queryKey: ['visits', 'weekly'] }, (old: unknown) => {
        if (!old || typeof old !== 'object') return old;
        const data = old as { visits?: Array<{ visitTargetId: number; visit?: { priority?: string; status?: string } }> };
        if (!data.visits) return old;
        return {
          ...data,
          visits: data.visits.map((item) =>
            item.visitTargetId === visitTargetId
              ? {
                  ...item,
                  visit: {
                    ...item.visit,
                    priority,
                    status: item.visit?.status || 'PENDING',
                  },
                }
              : item
          ),
        };
      });

      return { previousData };
    },
    onError: (error, _variables, context) => {
      // Rollback on error
      if (context?.previousData) {
        for (const [queryKey, data] of context.previousData) {
          queryClient.setQueryData(queryKey, data);
        }
      }
      toast.error('Ошибка изменения приоритета', { description: getErrorMessage(error) });
    },
    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: ['visits', 'weekly'] });
      queryClient.invalidateQueries({ queryKey: ['clientWeeklyVisits'] });
      queryClient.invalidateQueries({ queryKey: ['client-detail'] });
    },
  });
}

export function useCreateVisit() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: {
      visitTargetId: number;
      plannedDate?: string;
      priority?: VisitPriority;
      note?: string;
    }) => visitsApi.createVisit(params),
    onMutate: async (params) => {
      await queryClient.cancelQueries({ queryKey: ['visits', 'weekly'] });
      const previousData = queryClient.getQueriesData({ queryKey: ['visits', 'weekly'] });

      queryClient.setQueriesData({ queryKey: ['visits', 'weekly'] }, (old: unknown) => {
        if (!old || typeof old !== 'object') return old;
        const data = old as { visits?: Array<{ visitTargetId: number; visit?: { status?: string; priority?: string; plannedDate?: string } }> };
        if (!data.visits) return old;
        return {
          ...data,
          visits: data.visits.map((item) =>
            item.visitTargetId === params.visitTargetId
              ? {
                  ...item,
                  visit: {
                    ...item.visit,
                    status: 'PENDING',
                    priority: params.priority || item.visit?.priority || 'NORMAL',
                    plannedDate: params.plannedDate || item.visit?.plannedDate,
                  },
                }
              : item
          ),
        };
      });

      return { previousData };
    },
    onError: (error, _variables, context) => {
      if (context?.previousData) {
        for (const [queryKey, data] of context.previousData) {
          queryClient.setQueryData(queryKey, data);
        }
      }
      toast.error('Ошибка создания визита', { description: getErrorMessage(error) });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['visits', 'weekly'] });
      queryClient.invalidateQueries({ queryKey: ['clientWeeklyVisits'] });
      queryClient.invalidateQueries({ queryKey: ['client-detail'] });
    },
  });
}
